import {css, StyleSheet} from 'aphrodite';
import React from 'react';

// TODO(BNCH-76754): Move DropdownSelector to ui-library
import DropdownSelector from 'insights/analytics-visualizations/configs/form-components/DropdownSelector';
import {GetPipelineConfigSpecType, PipelineConfigJsonSpec} from 'pipelines/StepShapeUtils';
import {FONT_PROXIMA_14} from 'styles/FontConstants';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {ButtonSize} from 'ui-library/buttons/Button';
import {GRAY_LIGHTER} from 'ui-library/Colors';
import FieldError from 'ui-library/FieldError';
import {IntermediateValueControlledNumericInput} from 'ui-library/inputs/ControlledNumericInput';
import TextInput from 'ui-library/inputs/TextInput';
import {JSONArray, JSONObject, JSONValue} from 'util/Types';

import {EnumUISchema, ShapeUISchema, StepConfigUISchema, TaggedUnionUISchema} from '../StepConfigEditorTypes';
import ArrayEditor from './ArrayEditor';
import ColumnReferenceEditor from './ColumnReferenceEditor';
import EnumEditor from './EnumEditor';
import ShapeEditor from './ShapeEditor';
import TaggedUnionEditor from './TaggedUnionEditor';

/** @package */
export function getMinMaxErrorMessage(
  jsonValue: JSONValue,
  configShape: GetPipelineConfigSpecType<'integer'>
): string | null {
  if (jsonValue == null) {
    return null;
  } else if (typeof jsonValue === 'string' && !Number.isInteger(parseFloat(jsonValue))) {
    return 'Value must be a valid integer';
  }

  const value = Number(jsonValue);
  const min = configShape.minValue;
  const max = configShape.maxValue;

  if (min != null && max != null && (value < min || value > max)) {
    return `Value must be between ${min} and ${max}`;
  } else if (max != null && value > max) {
    return `Value must be less than ${max}`;
  } else if (min != null && value < min) {
    return `Value must be greater than ${min}`;
  }

  return null;
}

const BOOLEAN_EDITOR_OPTIONS = [
  {name: 'Yes', value: true},
  {name: 'No', value: false},
];

interface StepConfigEditorRouterProps {
  config: JSONValue;
  configShape: PipelineConfigJsonSpec;
  dataTestAttrs: Pick<DataTestAttrs, 'key' | 'index'>;
  onChangeConfig: (config: JSONValue) => void;
  uiSchema: StepConfigUISchema;
  disabled?: boolean;
  trashIcon?: JSX.Element;
  isTopLevel?: boolean;
}

export default function StepConfigEditorRouter({
  config,
  configShape,
  dataTestAttrs,
  onChangeConfig,
  uiSchema,
  disabled,
  trashIcon,
  isTopLevel,
}: StepConfigEditorRouterProps): JSX.Element {
  switch (configShape.kind) {
    case 'string':
      return (
        <div className={css(styles.oneRowWrapper)}>
          <TextInput
            className={css(styles.grow, styles.primitiveEditor)}
            dataTestAttrs={{component: 'StepConfigEditor', element: 'textInput', ...dataTestAttrs}}
            value={(config as string | null) ?? ''}
            placeholder="Text"
            onChange={(e) => {
              onChangeConfig(e.target.value);
            }}
            disabled={disabled}
          />
          {trashIcon && <div className={css(styles.oneRowTrashIconWrapper)}>{trashIcon}</div>}
        </div>
      );
    case 'boolean':
      return (
        <div className={css(styles.oneRowWrapper)}>
          <div className={css(styles.grow)}>
            <DropdownSelector<boolean>
              selectedValue={config as boolean | null}
              onChange={onChangeConfig}
              allowNull={!configShape.required}
              buttonSize={ButtonSize.MEDIUM}
              searchable={false}
              optionDatas={BOOLEAN_EDITOR_OPTIONS}
              disabled={disabled}
            />
          </div>
          {trashIcon && <div className={css(styles.oneRowTrashIconWrapper)}>{trashIcon}</div>}
        </div>
      );
    case 'number':
      return (
        <div className={css(styles.oneRowWrapper)}>
          <IntermediateValueControlledNumericInput
            onChange={(e) => {
              onChangeConfig(e.target.numericValue);
            }}
            value={config as number | null}
            placeholder="Number"
            className={css(styles.primitiveEditor)}
            isDisabled={disabled}
          />
          {trashIcon && <div className={css(styles.oneRowTrashIconWrapper)}>{trashIcon}</div>}
        </div>
      );
    case 'integer':
      return (
        <>
          <div className={css(styles.oneRowWrapper)}>
            <IntermediateValueControlledNumericInput
              onChange={(e) => {
                onChangeConfig(e.target.numericValue ?? (e.target.value || null));
              }}
              value={config as number | null}
              placeholder="Integer"
              className={css(styles.primitiveEditor)}
              validateInteger={true}
              min={configShape.minValue}
              max={configShape.maxValue}
              isDisabled={disabled}
            />
            {trashIcon && <div className={css(styles.oneRowTrashIconWrapper)}>{trashIcon}</div>}
          </div>
          {getMinMaxErrorMessage(config, configShape) && (
            <FieldError message={getMinMaxErrorMessage(config, configShape)} />
          )}
        </>
      );
    case 'enum':
      return (
        <div className={css(styles.oneRowWrapper, styles.grow)}>
          <EnumEditor
            // enums are always initialized to null in jsonSpecToInitialValue
            config={config as string | number | null}
            configShape={configShape}
            onChangeConfig={onChangeConfig}
            uiSchema={uiSchema as EnumUISchema | undefined}
            disabled={disabled}
          />
          {trashIcon && <div className={css(styles.oneRowTrashIconWrapper)}>{trashIcon}</div>}
        </div>
      );
    case 'columnReference':
      return (
        <div className={css(styles.oneRowWrapper, styles.grow)}>
          <div className={css(styles.grow)}>
            <ColumnReferenceEditor
              // column references are always initialized to null in jsonSpecToInitialValue
              config={config as string | null}
              configShape={configShape}
              dataTestAttrs={{component: 'StepConfigEditor', element: 'dropdown', ...dataTestAttrs}}
              onChangeConfig={onChangeConfig}
              disabled={disabled}
            />
          </div>
          {trashIcon && <div className={css(styles.oneRowTrashIconWrapper)}>{trashIcon}</div>}
        </div>
      );
    case 'array': {
      return (
        <ArrayEditor
          // arrays are always initialized to arrays with one element in jsonSpecToInitialValue, and so
          // are never null
          config={config as JSONArray}
          configShape={configShape}
          dataTestAttrs={dataTestAttrs}
          onChangeConfig={onChangeConfig}
          uiSchema={uiSchema}
          disabled={disabled}
        />
      );
    }
    case 'shape': {
      const editor = (
        <ShapeEditor
          configShape={configShape}
          // shapes are initialized to empty objects in jsonSpecToInitialValue, and so are never null
          config={config as JSONObject}
          onChangeConfig={onChangeConfig}
          uiSchema={uiSchema as ShapeUISchema | undefined}
          isTopLevel={isTopLevel}
          disabled={disabled}
        />
      );
      return isTopLevel ? (
        // When isTopLevel is true, there should never be a trashIcon since trashIcons are defined by parent
        // array editors and top level shapes have no parents.
        editor
      ) : (
        <div className={css(styles.subeditorWrapper)}>
          {editor}
          {trashIcon && <div className={css(styles.topRightTrashIcon)}>{trashIcon}</div>}
        </div>
      );
    }
    case 'taggedUnion': {
      return (
        <div className={css(styles.subeditorWrapper)}>
          <TaggedUnionEditor
            configShape={configShape}
            // tagged unions are initialized to null in jsonSpecToInitialValue, then become objects once
            // a tag key is selected
            config={config as JSONObject | null}
            onChangeConfig={onChangeConfig}
            uiSchema={uiSchema as TaggedUnionUISchema | undefined}
            disabled={disabled}
          />
          {trashIcon && <div className={css(styles.topRightTrashIcon)}>{trashIcon}</div>}
        </div>
      );
    }
    default:
      // TODO(BNCH-75415): Implement other kinds, except anything
      return <div>Unsupported</div>;
  }
}

const styles = StyleSheet.create({
  oneRowWrapper: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 8,
  },
  grow: {
    flexGrow: 1,
  },
  oneRowTrashIconWrapper: {
    marginLeft: 6,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  primitiveEditor: {
    ...FONT_PROXIMA_14,
    height: 36,
    borderRadius: 4,
  },
  subeditorWrapper: {
    background: GRAY_LIGHTER,
    // use relative positioning so that any absolute-ly positioned trash icons rendered inside the shape
    // editor will be positioned relative to the shape editor
    position: 'relative',
    borderRadius: 2,
    padding: 12,
    marginBottom: 10,
  },
  topRightTrashIcon: {
    position: 'absolute',
    top: 10,
    right: 10,
  },
});
